import { Link } from 'react-router-dom';
import { MapPin, Twitter, Linkedin, Youtube, Instagram, Facebook } from 'lucide-react';
import { NewsletterSignup } from './NewsletterSignup';

const FOOTER_COLUMNS = {
  Products: [
    { name: 'Compute', href: '/products/compute' },
    { name: 'Storage', href: '/products/storage' },
    { name: 'Networking', href: '/products/networking' },
    { name: 'Cloud Security', href: '/products/cloud-security' },
    { name: 'Backup & DR', href: '/products/backup-dr' },
    { name: 'Managed Cloud', href: '/products/managed-cloud' },
    { name: 'Full Service Catalog', href: '/services' }
  ],
  Solutions: [
    { name: 'Sovereign Cloud', href: '/solutions/sovereign-cloud' },
    { name: 'Enterprise Cloud', href: '/solutions#enterprise' },
    { name: 'Government', href: '/solutions#government' },
    { name: 'Critical Infrastructure', href: '/solutions#critical-infrastructure' },
    { name: 'MSPs', href: '/solutions#msps' },
    { name: 'SMEs & Startups', href: '/solutions#smes-startups' },
    { name: 'Industries We Support', href: '/solutions' }
  ],
  Resources: [
    { name: 'Insights', href: '/insights' },
    { name: 'Cloud by Country', href: '/cloud' },
    { name: 'Compare Clouds', href: '/compare' },
    { name: 'Documentation', href: '/docs/getting-started/introduction' },
    { name: 'Support', href: '/support' },
    { name: 'Cloud Status', href: '/status' },
    { name: 'Trust Center', href: '/trust' },
    { name: 'Regions', href: '/regions' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Full Service Catalog', href: '/services' }
  ],
  Customer: [
    { name: 'Request Cloud Access', href: '/cloud-access' },
    { name: 'Cloud Console', href: '/console' },
    { name: 'Support', href: '/support' },
    { name: 'Documentation', href: '/docs/getting-started/introduction' },
    { name: 'Cloud Status', href: '/status' },
    { name: 'Legal & Policies', href: '/legal' }
  ],
  Company: [
    { name: 'Platform', href: '/platform' },
    { name: 'Why DAAKYI', href: '/why-daakyi' },
    { name: 'About', href: '/about' },
    { name: 'Security', href: '/security' },
    { name: 'Migrate from VMware', href: '/migrate' },
    { name: 'Resource Library', href: '/resources' },
    { name: 'Contact', href: '/contact' }
  ]
};

const OFFICE_LOCATIONS = [
  { city: 'Accra', tag: 'Cloud Region · HQ' },
  { city: 'Houston', tag: 'Office' },
  { city: 'Dubai', tag: 'Office' }
];

const SOCIAL_LINKS = [
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com/DaakyiC' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/daakyi-consulting/' },
  { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCOUo4n1gERtv7JzAsyRo71Q' },
  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/daakyi_consulting/' },
  { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100083481409009' }
];

const FooterBrand = () => (
        <div className="col-span-2">
          <Link to="/" className="inline-flex flex-col items-start mb-4">
            <img src="/daakyi-logo-nav.png" alt="DAAKYI Cloud" className="h-8" />
          </Link>
          <p className="text-[#93A5BC] text-sm leading-relaxed mb-5 max-w-[280px]">
            Secure cloud infrastructure and cybersecurity services built for African organizations and designed to meet global standards.
          </p>
          <div className="flex items-center gap-4 mb-5 flex-wrap">
            {OFFICE_LOCATIONS.map(loc => (
              <span key={loc.city} className="flex items-center gap-1 text-[#93A5BC] text-xs">
                <MapPin className="w-3 h-3 text-[#F29B3D]" strokeWidth={1.5} />
                {loc.city}{loc.tag && <span className="text-[#F29B3D] text-[9px] font-bold ml-0.5">{loc.tag}</span>}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {SOCIAL_LINKS.map(({ icon: Icon, label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-[#93A5BC] hover:text-white hover:border-white/25 transition-all">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>
);

const FooterOffices = () => (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-7 grid grid-cols-1 sm:grid-cols-3 gap-5 text-xs text-[#94A3B8]" data-testid="footer-offices">
        <div>
          <span className="text-[#93A5BC] font-semibold block mb-1">Accra — Headquarters</span>
          14 Griddle Street, Teshie-Nungua<br />
          Accra, Ghana · GPS: GZ099-0706<br />
          <a href="tel:+233598444207" className="hover:text-white transition-colors">+233 59 844 4207</a> ·{' '}
          <a href="mailto:cloud@daakyi.com" className="hover:text-white transition-colors">cloud@daakyi.com</a>
        </div>
        <div>
          <span className="text-[#93A5BC] font-semibold block mb-1">Houston — Americas Office</span>
          4606 FM 1960 Suite 211<br />
          Houston, Texas 77069, United States<br />
          <a href="tel:+18888531350" className="hover:text-white transition-colors">+1 (888) 853-1350</a>
        </div>
        <div>
          <span className="text-[#93A5BC] font-semibold block mb-1">Dubai — Gulf Office</span>
          Rolex Towers, 9th Floor, Sheikh Zayed Road<br />
          Near Financial Center Metro Station<br />
          Dubai, United Arab Emirates
        </div>
      </div>
);

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="relative bg-[#0B0524] border-t border-white/[0.06]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 lg:py-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-10 lg:gap-8">
          {/* Brand */}
          <FooterBrand />

          {/* Columns */}
          {Object.entries(FOOTER_COLUMNS).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-semibold text-white/80 text-xs uppercase tracking-wider mb-4">{title}</h4>
              <ul className="space-y-2.5">
                {links.map(link => (
                  <li key={link.name}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-[#93A5BC] hover:text-white text-sm transition-colors">{link.name}</a>
                    ) : (
                      <Link to={link.href} className="text-[#93A5BC] hover:text-white text-sm transition-colors">{link.name}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <NewsletterSignup />

      {/* Bottom */}
      <div className="border-t border-white/[0.06] relative z-10">
        <FooterOffices />
      </div>
      <div className="border-t border-white/[0.06] relative z-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[#94A3B8] text-xs">
              © {new Date().getFullYear()} DAAKYI Secure Africa LTD. Headquartered in Accra, Ghana. All rights reserved.
              {' · '}
              <Link to="/fr" data-testid="footer-fr-link" className="hover:text-white transition-colors">Français</Link>
            </p>
            <div className="flex items-center gap-5 text-xs">
              <Link to="/privacy" className="text-[#94A3B8] hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="text-[#94A3B8] hover:text-white transition-colors">Terms</Link>
              <Link to="/security" className="text-[#94A3B8] hover:text-white transition-colors">Security</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
